import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { 
  ArrowLeft, Plus, Trash2, Save, CheckCircle, Eye,
  Layers, BookOpen, FileText, X, Upload, Circle
} from 'lucide-react';
import { toast } from 'sonner';
import MathTextarea from '../components/MathTextarea';

const QuestionEditor = () => {
  const navigate = useNavigate();
  const { questionId } = useParams();
  const isEditMode = questionId && questionId !== 'new';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Reference data
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [papers, setPapers] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  
  // Quick Add Modal States
  const [showAddTopicModal, setShowAddTopicModal] = useState(false);
  const [showAddSubtopicModal, setShowAddSubtopicModal] = useState(false);
  const [addingTopic, setAddingTopic] = useState(false);
  const [addingSubtopic, setAddingSubtopic] = useState(false);
  const [newTopicData, setNewTopicData] = useState({ name: '', code: '' });
  const [newSubtopicData, setNewSubtopicData] = useState({ name: '', code: '' });
  
  // Question data
  const [questionData, setQuestionData] = useState({
    board_id: '',
    subject_id: '',
    paper_id: '',
    topic_id: '',
    subtopic_id: '',
    question_number: 1,
    question_type: 'structured',
    question_text: '',
    question_diagram: null,
    mcq_options: [
      { label: 'A', text: '', is_correct: false, option_image: null },
      { label: 'B', text: '', is_correct: false, option_image: null },
      { label: 'C', text: '', is_correct: false, option_image: null },
      { label: 'D', text: '', is_correct: false, option_image: null },
    ],
    correct_option: null,
    negative_marks: 0,
    parts: [],
    total_marks: 0,
    difficulty_level: 'medium',
    year: null,
    session: '',
    variant: '',
    tags: [],
    status: 'draft'
  });

  useEffect(() => {
    fetchReferenceData();
    if (isEditMode) {
      fetchQuestion();
    } else {
      setLoading(false);
    }
  }, [questionId]);

  // Fetch topics when board/subject/paper changes - reset topic selection when dependencies change
  useEffect(() => {
    if (questionData.board_id && questionData.subject_id) {
      fetchTopics();
    } else {
      setTopics([]);
      setSubtopics([]);
    }
  }, [questionData.board_id, questionData.subject_id, questionData.paper_id]);

  // Fetch subtopics when topic changes
  useEffect(() => {
    if (questionData.topic_id) {
      fetchSubtopics();
    } else {
      setSubtopics([]);
    }
  }, [questionData.topic_id]);

  // Reset dependent fields when parent changes
  const handleBoardChange = (boardId) => {
    setQuestionData(prev => ({
      ...prev,
      board_id: boardId,
      paper_id: '',
      topic_id: '',
      subtopic_id: ''
    }));
    setTopics([]);
    setSubtopics([]);
  };

  const handleSubjectChange = (subjectId) => {
    setQuestionData(prev => ({
      ...prev,
      subject_id: subjectId,
      paper_id: '',
      topic_id: '',
      subtopic_id: ''
    }));
    setTopics([]);
    setSubtopics([]);
  };

  const handlePaperChange = (paperId) => {
    setQuestionData(prev => ({
      ...prev,
      paper_id: paperId,
      topic_id: '',
      subtopic_id: ''
    }));
    // Topics will be re-fetched by useEffect
  };

  const handleTopicChange = (topicId) => {
    setQuestionData(prev => ({
      ...prev,
      topic_id: topicId,
      subtopic_id: ''
    }));
    setSubtopics([]);
  };

  // Quick Add Topic Handler
  const handleQuickAddTopic = async (e) => {
    e.preventDefault();
    if (!newTopicData.name.trim()) {
      toast.error('Topic name is required');
      return;
    }
    if (!questionData.board_id || !questionData.subject_id) {
      toast.error('Please select Board and Subject first');
      return;
    }

    setAddingTopic(true);
    try {
      const response = await api.post('/question-bank/topics', {
        name: newTopicData.name.trim(),
        code: newTopicData.code.trim() || null,
        board_id: questionData.board_id,
        subject_id: questionData.subject_id,
        paper_id: questionData.paper_id || null
      });
      
      toast.success('Topic created successfully');
      
      // Refresh topics and select the new one
      await fetchTopics();
      if (response.data.topic?.id) {
        setQuestionData(prev => ({ ...prev, topic_id: response.data.topic.id }));
      }
      
      setShowAddTopicModal(false);
      setNewTopicData({ name: '', code: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create topic');
    } finally {
      setAddingTopic(false);
    }
  };

  // Quick Add Subtopic Handler
  const handleQuickAddSubtopic = async (e) => {
    e.preventDefault();
    if (!newSubtopicData.name.trim()) {
      toast.error('Subtopic name is required');
      return;
    }
    if (!questionData.topic_id) {
      toast.error('Please select a Topic first');
      return;
    }

    setAddingSubtopic(true);
    try {
      const response = await api.post('/question-bank/subtopics', {
        name: newSubtopicData.name.trim(),
        code: newSubtopicData.code.trim() || null,
        topic_id: questionData.topic_id
      });
      
      toast.success('Subtopic created successfully');
      
      // Refresh subtopics and select the new one
      await fetchSubtopics();
      if (response.data.subtopic?.id) {
        setQuestionData(prev => ({ ...prev, subtopic_id: response.data.subtopic.id }));
      }
      
      setShowAddSubtopicModal(false);
      setNewSubtopicData({ name: '', code: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create subtopic');
    } finally {
      setAddingSubtopic(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [boardsRes, subjectsRes, papersRes] = await Promise.all([
        api.get('/boards'),
        api.get('/subjects'),
        api.get('/question-bank/papers')
      ]);
      setBoards(boardsRes.data);
      setSubjects(subjectsRes.data);
      setPapers(papersRes.data);
    } catch (err) {
      toast.error('Failed to load reference data');
    }
  };

  const fetchTopics = async () => {
    try {
      const params = new URLSearchParams();
      params.append('board_id', questionData.board_id);
      params.append('subject_id', questionData.subject_id);
      if (questionData.paper_id) params.append('paper_id', questionData.paper_id);
      
      const res = await api.get(`/question-bank/topics?${params}`);
      setTopics(res.data);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  };

  const fetchSubtopics = async () => {
    try {
      const res = await api.get(`/question-bank/subtopics?topic_id=${questionData.topic_id}`);
      setSubtopics(res.data);
    } catch (err) {
      console.error('Failed to fetch subtopics:', err);
    }
  };

  const fetchQuestion = async () => {
    try {
      const res = await api.get(`/question-bank/questions/${questionId}`);
      const data = res.data;
      
      // Ensure all required fields exist with defaults
      const sanitizedData = {
        board_id: data.board_id || '',
        subject_id: data.subject_id || '',
        paper_id: data.paper_id || '',
        topic_id: data.topic_id || '',
        subtopic_id: data.subtopic_id || '',
        question_number: data.question_number || 1,
        question_type: data.question_type || 'structured',
        question_text: data.question_text || '',
        question_diagram: data.question_diagram || null,
        mcq_options: data.mcq_options || [
          { label: 'A', text: '', is_correct: false, option_image: null },
          { label: 'B', text: '', is_correct: false, option_image: null },
          { label: 'C', text: '', is_correct: false, option_image: null },
          { label: 'D', text: '', is_correct: false, option_image: null },
        ],
        correct_option: data.correct_option !== undefined ? data.correct_option : null,
        negative_marks: data.negative_marks || 0,
        parts: (data.parts || []).map(part => ({
          part_label: part.part_label || 'a',
          part_text: part.part_text || '',
          marks: part.marks !== undefined ? part.marks : 2,
          assessment_objective: part.assessment_objective || 'AO1',
          has_drawing: part.has_drawing || false,
          diagram_base64: part.diagram_base64 || null,
          sub_parts: part.sub_parts && part.sub_parts.length > 0 
            ? part.sub_parts.map(sp => ({
                sub_part_label: sp.sub_part_label || 'i',
                sub_part_text: sp.sub_part_text || '',
                marks: sp.marks !== undefined ? sp.marks : 1,
                assessment_objective: sp.assessment_objective || 'AO1',
                has_drawing: sp.has_drawing || false,
                diagram_base64: sp.diagram_base64 || null,
                model_answer: sp.model_answer || '',
                marking_scheme: sp.marking_scheme || ''
              }))
            : null,
          model_answer: part.model_answer || '',
          marking_scheme: part.marking_scheme || ''
        })),
        total_marks: data.total_marks || data.marks || 0,
        difficulty_level: data.difficulty_level || data.difficulty || 'medium',
        year: data.year || null,
        session: data.session || '',
        variant: data.variant || '',
        tags: data.tags || [],
        status: data.status || 'draft',
        // Keep original fields for compatibility
        ...data
      };
      
      setQuestionData(sanitizedData);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.response?.data?.detail || 'Failed to load question. Please try again.');
      toast.error('Failed to load question');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setQuestionData(prev => ({ ...prev, [field]: value }));
  };

  // Part management
  const addPart = () => {
    const currentParts = questionData.parts || [];
    const newPart = {
      part_label: String.fromCharCode(97 + currentParts.length), // a, b, c...
      part_text: '',
      marks: 2,
      assessment_objective: 'AO1',
      has_drawing: false,
      diagram_base64: null,
      sub_parts: null,
      model_answer: '',
      marking_scheme: ''
    };
    setQuestionData(prev => ({
      ...prev,
      parts: [...(prev.parts || []), newPart]
    }));
  };

  const updatePart = (partIndex, field, value) => {
    setQuestionData(prev => {
      const newParts = [...(prev.parts || [])];
      newParts[partIndex] = { ...newParts[partIndex], [field]: value };
      return { ...prev, parts: newParts };
    });
  };

  const removePart = (partIndex) => {
    setQuestionData(prev => ({
      ...prev,
      parts: (prev.parts || []).filter((_, idx) => idx !== partIndex)
    }));
  };

  // Sub-part management
  const addSubPart = (partIndex) => {
    setQuestionData(prev => {
      const newParts = [...(prev.parts || [])];
      const currentSubParts = newParts[partIndex]?.sub_parts || [];
      const romanNumerals = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
      
      newParts[partIndex] = {
        ...newParts[partIndex],
        marks: null, // Clear part marks when using sub-parts
        sub_parts: [
          ...currentSubParts,
          {
            sub_part_label: romanNumerals[currentSubParts.length] || `${currentSubParts.length + 1}`,
            sub_part_text: '',
            marks: 1,
            assessment_objective: 'AO1',
            has_drawing: false,
            diagram_base64: null,
            model_answer: '',
            marking_scheme: ''
          }
        ]
      };
      return { ...prev, parts: newParts };
    });
  };

  const updateSubPart = (partIndex, subPartIndex, field, value) => {
    setQuestionData(prev => {
      const newParts = [...(prev.parts || [])];
      const newSubParts = [...(newParts[partIndex]?.sub_parts || [])];
      newSubParts[subPartIndex] = { ...newSubParts[subPartIndex], [field]: value };
      newParts[partIndex] = { ...newParts[partIndex], sub_parts: newSubParts };
      return { ...prev, parts: newParts };
    });
  };

  const removeSubPart = (partIndex, subPartIndex) => {
    setQuestionData(prev => {
      const newParts = [...(prev.parts || [])];
      const newSubParts = (newParts[partIndex]?.sub_parts || []).filter((_, idx) => idx !== subPartIndex);
      newParts[partIndex] = {
        ...newParts[partIndex],
        sub_parts: newSubParts.length > 0 ? newSubParts : null
      };
      return { ...prev, parts: newParts };
    });
  };

  // MCQ management
  const updateMCQOption = (index, field, value) => {
    setQuestionData(prev => {
      const newOptions = [...(prev.mcq_options || [])];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return { ...prev, mcq_options: newOptions };
    });
  };

  const setCorrectOption = (index) => {
    setQuestionData(prev => {
      const newOptions = prev.mcq_options.map((opt, idx) => ({
        ...opt,
        is_correct: idx === index
      }));
      return { ...prev, mcq_options: newOptions, correct_option: index };
    });
  };

  // Image upload handlers
  const handleQuestionDiagramUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      updateField('question_diagram', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleMCQOptionImageUpload = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      updateMCQOption(index, 'option_image', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handlePartDiagramUpload = (partIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      updatePart(partIndex, 'diagram_base64', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubPartDiagramUpload = (partIndex, subPartIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      updateSubPart(partIndex, subPartIndex, 'diagram_base64', reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Calculate total marks
  const calculateTotalMarks = () => {
    if (questionData.question_type === 'mcq') {
      return questionData.total_marks || 1;
    }
    
    let total = 0;
    (questionData.parts || []).forEach(part => {
      if (part.sub_parts && part.sub_parts.length > 0) {
        total += part.sub_parts.reduce((sum, sp) => sum + (sp.marks || 0), 0);
      } else {
        total += part.marks || 0;
      }
    });
    return total;
  };

  const handleSave = async (publish = false) => {
    try {
      // Validation - check for either IDs or name fields (for imported questions)
      const hasBoard = questionData.board_id || questionData.board || questionData.board_name;
      const hasSubject = questionData.subject_id || questionData.subject || questionData.subject_name;
      const hasPaper = questionData.paper_id || questionData.paper || questionData.paper_code;
      const hasTopic = questionData.topic_id || questionData.topic || questionData.topic_name;
      
      if (!hasBoard || !hasSubject || !hasPaper || !hasTopic) {
        toast.error('Please fill in all required fields (Board, Subject, Paper, Topic)');
        return;
      }
      
      if (!questionData.question_text || !questionData.question_text.trim()) {
        toast.error('Please enter question text');
        return;
      }
      
      if (questionData.question_type === 'structured' && (!questionData.parts || questionData.parts.length === 0)) {
        toast.error('Please add at least one part to the question');
        return;
      }
      
      if (questionData.question_type === 'mcq' && questionData.correct_option === null) {
        toast.error('Please select the correct answer for MCQ');
        return;
      }

      setSaving(true);
      
      const dataToSave = {
        ...questionData,
        total_marks: calculateTotalMarks(),
        status: publish ? 'published' : questionData.status
      };
      
      if (isEditMode) {
        await api.put(`/question-bank/questions/${questionId}`, dataToSave);
        toast.success('Question updated successfully');
      } else {
        await api.post('/question-bank/questions', dataToSave);
        toast.success('Question created successfully');
      }
      
      navigate('/qbm/dashboard');
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">!</div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Error Loading Question</h2>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/qbm/dashboard')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/qbm/dashboard')}
                className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-slate-900">
                {isEditMode ? 'Edit Question' : 'Create Question'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                Save Draft
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save & Publish'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="col-span-2 space-y-6">
            {/* Question Type Toggle */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Question Type</h2>
              <div className="flex gap-4">
                <button
                  onClick={() => updateField('question_type', 'structured')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    questionData.question_type === 'structured'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <FileText className={`w-6 h-6 mx-auto mb-2 ${questionData.question_type === 'structured' ? 'text-indigo-600' : 'text-slate-400'}`} />
                  <div className="font-medium text-slate-900">Structured</div>
                  <div className="text-sm text-slate-500">Parts & sub-parts</div>
                </button>
                <button
                  onClick={() => updateField('question_type', 'mcq')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    questionData.question_type === 'mcq'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <CheckCircle className={`w-6 h-6 mx-auto mb-2 ${questionData.question_type === 'mcq' ? 'text-indigo-600' : 'text-slate-400'}`} />
                  <div className="font-medium text-slate-900">MCQ</div>
                  <div className="text-sm text-slate-500">Multiple choice</div>
                </button>
              </div>
            </div>

            {/* Question Text */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Question Text</h2>
              <MathTextarea
                value={questionData.question_text}
                onChange={(val) => updateField('question_text', val)}
                rows={4}
                placeholder="Enter the question text here... (Use toolbar for math symbols)"
              />
            </div>

            {/* MCQ Options */}
            {questionData.question_type === 'mcq' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Answer Options</h2>
                
                {/* Question Diagram for MCQ */}
                <div className="mb-6 pb-6 border-b border-slate-200">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Question Diagram (Optional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleQuestionDiagramUpload}
                    className="hidden"
                    id="question-diagram-upload"
                  />
                  <label
                    htmlFor="question-diagram-upload"
                    className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-indigo-400 transition-colors"
                  >
                    <Upload className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-500">Upload diagram for question</span>
                  </label>
                  {questionData.question_diagram && (
                    <div className="mt-3 relative inline-block">
                      <img
                        src={questionData.question_diagram}
                        alt="Question diagram"
                        className="max-h-48 rounded-lg border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => updateField('question_diagram', null)}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                {/* MCQ Settings */}
                <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-slate-200">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Marks
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={questionData.total_marks || 1}
                      onChange={(e) => updateField('total_marks', parseInt(e.target.value) || 1)}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Negative Marks
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      min="0"
                      value={questionData.negative_marks || 0}
                      onChange={(e) => updateField('negative_marks', parseFloat(e.target.value) || 0)}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Assessment Objective
                    </label>
                    <select
                      value={questionData.assessment_objective || 'AO1'}
                      onChange={(e) => updateField('assessment_objective', e.target.value)}
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="AO1">AO1 - Knowledge</option>
                      <option value="AO2">AO2 - Application</option>
                      <option value="AO3">AO3 - Analysis</option>
                    </select>
                  </div>
                </div>
                
                {/* Options */}
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Options (Click circle to mark correct answer)
                </label>
                <div className="space-y-3">
                  {(questionData.mcq_options || []).map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-start gap-3 p-3 rounded-lg border-2 transition-all ${
                        opt.is_correct 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => setCorrectOption(idx)}
                        className="mt-1 flex-shrink-0"
                      >
                        {opt.is_correct ? (
                          <CheckCircle className="w-6 h-6 text-green-600" />
                        ) : (
                          <Circle className="w-6 h-6 text-slate-300" />
                        )}
                      </button>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-700">{opt.label}.</span>
                          <input
                            type="text"
                            value={opt.text}
                            onChange={(e) => updateMCQOption(idx, 'text', e.target.value)}
                            placeholder={`Option ${opt.label} text...`}
                            className="flex-1 h-10 px-3 rounded border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        {/* Option Image Upload */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleMCQOptionImageUpload(idx, e)}
                            className="hidden"
                            id={`option-image-${idx}`}
                          />
                          <label
                            htmlFor={`option-image-${idx}`}
                            className="text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer flex items-center gap-1"
                          >
                            <Upload className="w-3 h-3" />
                            {opt.option_image ? 'Change image' : 'Add image'}
                          </label>
                          {opt.option_image && (
                            <>
                              <img
                                src={opt.option_image}
                                alt={`Option ${opt.label}`}
                                className="h-16 rounded border border-slate-200"
                              />
                              <button
                                type="button"
                                onClick={() => updateMCQOption(idx, 'option_image', null)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {questionData.correct_option === null && (
                  <p className="mt-3 text-sm text-amber-600">
                    Please click on the circle to mark the correct answer
                  </p>
                )}
              </div>
            )}

            {/* Structured Parts */}
            {questionData.question_type === 'structured' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">Parts</h2>
                  <button
                    onClick={addPart}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100"
                  >
                    <Plus className="w-4 h-4" />
                    Add Part
                  </button>
                </div>

                {(!questionData.parts || questionData.parts.length === 0) ? (
                  <div className="text-center py-8 text-slate-500">
                    <Layers className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No parts added yet. Click "Add Part" to start.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {(questionData.parts || []).map((part, pIndex) => (
                      <div key={pIndex} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-semibold text-indigo-600">Part {part.part_label})</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => addSubPart(pIndex)}
                              className="text-xs text-amber-600 hover:text-amber-700 px-2 py-1 bg-amber-50 rounded"
                            >
                              + Add Sub-parts (i, ii, iii)
                            </button>
                            <button
                              onClick={() => removePart(pIndex)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        <div className="mb-3">
                          <MathTextarea
                            value={part.part_text}
                            onChange={(val) => updatePart(pIndex, 'part_text', val)}
                            rows={2}
                            placeholder="Part text..."
                          />
                        </div>

                        {/* Part controls (when no sub-parts) */}
                        {(!part.sub_parts || part.sub_parts.length === 0) && (
                          <>
                            <div className="flex items-center gap-4 flex-wrap mb-3">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={part.has_drawing}
                                  onChange={(e) => updatePart(pIndex, 'has_drawing', e.target.checked)}
                                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <span className="text-sm text-slate-600">Requires drawing</span>
                              </label>

                              <label className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-all">
                                <Upload className="w-4 h-4 text-slate-600" />
                                <span className="text-sm text-slate-600">
                                  {part.diagram_base64 ? 'Change Diagram' : 'Upload Diagram'}
                                </span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handlePartDiagramUpload(pIndex, e)}
                                  className="hidden"
                                />
                              </label>

                              <div className="flex items-center gap-2">
                                <label className="text-sm text-slate-600">Marks:</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={part.marks || ''}
                                  onChange={(e) => updatePart(pIndex, 'marks', parseInt(e.target.value) || 0)}
                                  className="w-16 h-8 px-2 text-center rounded border border-slate-200"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-sm text-slate-600">AO:</label>
                                <select
                                  value={part.assessment_objective || ''}
                                  onChange={(e) => updatePart(pIndex, 'assessment_objective', e.target.value)}
                                  className="h-8 px-2 rounded border border-slate-200"
                                >
                                  <option value="">-</option>
                                  <option value="AO1">AO1</option>
                                  <option value="AO2">AO2</option>
                                  <option value="AO3">AO3</option>
                                </select>
                              </div>
                            </div>

                            {part.diagram_base64 && (
                              <div className="relative inline-block mb-3">
                                <img
                                  src={part.diagram_base64}
                                  alt="Diagram preview"
                                  className="max-w-xs rounded-lg border border-slate-200"
                                />
                                <button
                                  type="button"
                                  onClick={() => updatePart(pIndex, 'diagram_base64', null)}
                                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            )}

                            {/* Marking Scheme for Part */}
                            <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <label className="block text-sm font-medium text-amber-800 mb-2">
                                Marking Scheme
                              </label>
                              <textarea
                                value={part.marking_scheme || ''}
                                onChange={(e) => updatePart(pIndex, 'marking_scheme', e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                                placeholder="Enter marking points (e.g., 1 mark for correct formula, 1 mark for substitution...)"
                              />
                              <label className="block text-sm font-medium text-amber-800 mt-2 mb-1">
                                Model Answer
                              </label>
                              <textarea
                                value={part.model_answer || ''}
                                onChange={(e) => updatePart(pIndex, 'model_answer', e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                                placeholder="Enter model answer"
                              />
                            </div>
                          </>
                        )}

                        {/* Sub-parts */}
                        {part.sub_parts && part.sub_parts.length > 0 && (
                          <div className="mt-4 ml-4 space-y-3 pl-4 border-l-2 border-amber-300">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                                Sub-parts (IGCSE)
                              </span>
                            </div>
                            {part.sub_parts.map((sp, spIndex) => (
                              <div key={spIndex} className="p-3 bg-white rounded border border-amber-200">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-amber-600">({sp.sub_part_label})</span>
                                  <button
                                    onClick={() => removeSubPart(pIndex, spIndex)}
                                    className="text-red-400 hover:text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                                <div className="mb-2">
                                  <MathTextarea
                                    value={sp.sub_part_text}
                                    onChange={(val) => updateSubPart(pIndex, spIndex, 'sub_part_text', val)}
                                    rows={2}
                                    placeholder="Sub-part text..."
                                  />
                                </div>
                                <div className="flex items-center gap-4 flex-wrap mb-2">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={sp.has_drawing}
                                      onChange={(e) => updateSubPart(pIndex, spIndex, 'has_drawing', e.target.checked)}
                                      className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                    />
                                    <span className="text-xs text-slate-600">Drawing</span>
                                  </label>

                                  <label className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 cursor-pointer">
                                    <Upload className="w-3 h-3" />
                                    <span>{sp.diagram_base64 ? 'Change' : 'Diagram'}</span>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => handleSubPartDiagramUpload(pIndex, spIndex, e)}
                                      className="hidden"
                                    />
                                  </label>

                                  <div className="flex items-center gap-1">
                                    <label className="text-xs text-slate-600">Marks:</label>
                                    <input
                                      type="number"
                                      min="1"
                                      value={sp.marks}
                                      onChange={(e) => updateSubPart(pIndex, spIndex, 'marks', parseInt(e.target.value) || 1)}
                                      className="w-14 h-6 px-1 text-xs text-center rounded border border-slate-200"
                                    />
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <label className="text-xs text-slate-600">AO:</label>
                                    <select
                                      value={sp.assessment_objective || ''}
                                      onChange={(e) => updateSubPart(pIndex, spIndex, 'assessment_objective', e.target.value)}
                                      className="h-6 px-1 text-xs rounded border border-slate-200"
                                    >
                                      <option value="">-</option>
                                      <option value="AO1">AO1</option>
                                      <option value="AO2">AO2</option>
                                      <option value="AO3">AO3</option>
                                    </select>
                                  </div>
                                </div>
                                {sp.diagram_base64 && (
                                  <div className="relative inline-block">
                                    <img
                                      src={sp.diagram_base64}
                                      alt="Sub-part diagram"
                                      className="max-h-32 rounded border border-slate-200"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => updateSubPart(pIndex, spIndex, 'diagram_base64', null)}
                                      className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}

                                {/* Marking Scheme for Sub-part */}
                                <div className="mt-2 p-2 bg-amber-50/50 rounded border border-amber-200">
                                  <label className="block text-xs font-medium text-amber-800 mb-1">
                                    Marking Scheme
                                  </label>
                                  <textarea
                                    value={sp.marking_scheme || ''}
                                    onChange={(e) => updateSubPart(pIndex, spIndex, 'marking_scheme', e.target.value)}
                                    rows={2}
                                    className="w-full px-2 py-1.5 text-xs rounded border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                                    placeholder="Marking points..."
                                  />
                                  <label className="block text-xs font-medium text-amber-800 mt-1.5 mb-1">
                                    Model Answer
                                  </label>
                                  <textarea
                                    value={sp.model_answer || ''}
                                    onChange={(e) => updateSubPart(pIndex, spIndex, 'model_answer', e.target.value)}
                                    rows={1}
                                    className="w-full px-2 py-1.5 text-xs rounded border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                                    placeholder="Expected answer..."
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="col-span-1 space-y-6">
            {/* Classification */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Classification</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Board *</label>
                  <select
                    value={questionData.board_id}
                    onChange={(e) => handleBoardChange(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Board</option>
                    {boards.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
                  <select
                    value={questionData.subject_id}
                    onChange={(e) => handleSubjectChange(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Subject</option>
                    {subjects.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Paper *</label>
                  <select
                    value={questionData.paper_id}
                    onChange={(e) => handlePaperChange(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Paper</option>
                    {papers
                      .filter(p => !questionData.board_id || p.board_id === questionData.board_id)
                      .filter(p => !questionData.subject_id || p.subject_id === questionData.subject_id)
                      .map(p => (
                        <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
                      ))
                    }
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-slate-700">Topic *</label>
                    {questionData.board_id && questionData.subject_id && (
                      <button
                        type="button"
                        onClick={() => setShowAddTopicModal(true)}
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                        data-testid="quick-add-topic-btn"
                      >
                        <Plus className="w-3 h-3" />
                        Quick Add
                      </button>
                    )}
                  </div>
                  <select
                    value={questionData.topic_id}
                    onChange={(e) => handleTopicChange(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={!questionData.board_id || !questionData.subject_id}
                  >
                    <option value="">{topics.length === 0 && questionData.board_id && questionData.subject_id ? 'No topics available' : 'Select Topic'}</option>
                    {topics.map(t => (
                      <option key={t.id} value={t.id}>{t.code ? `${t.code} - ` : ''}{t.name}</option>
                    ))}
                  </select>
                  {questionData.board_id && questionData.subject_id && topics.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No topics found. Click "Quick Add" above to create one.</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-slate-700">Subtopic</label>
                    {questionData.topic_id && (
                      <button
                        type="button"
                        onClick={() => setShowAddSubtopicModal(true)}
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                        data-testid="quick-add-subtopic-btn"
                      >
                        <Plus className="w-3 h-3" />
                        Quick Add
                      </button>
                    )}
                  </div>
                  <select
                    value={questionData.subtopic_id || ''}
                    onChange={(e) => updateField('subtopic_id', e.target.value || null)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={!questionData.topic_id}
                  >
                    <option value="">{subtopics.length === 0 && questionData.topic_id ? 'No subtopics available' : 'Select Subtopic (optional)'}</option>
                    {subtopics.map(st => (
                      <option key={st.id} value={st.id}>{st.code ? `${st.code} - ` : ''}{st.name}</option>
                    ))}
                  </select>
                  {questionData.topic_id && subtopics.length === 0 && (
                    <p className="text-xs text-slate-500 mt-1">No subtopics. Click "Quick Add" to create one.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Question Number</label>
                  <input
                    type="number"
                    min="1"
                    value={questionData.question_number}
                    onChange={(e) => updateField('question_number', parseInt(e.target.value) || 1)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Metadata */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Metadata</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Difficulty</label>
                  <select
                    value={questionData.difficulty_level || ''}
                    onChange={(e) => updateField('difficulty_level', e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200"
                  >
                    <option value="">Not specified</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                  <input
                    type="number"
                    placeholder="e.g., 2023"
                    value={questionData.year || ''}
                    onChange={(e) => updateField('year', parseInt(e.target.value) || null)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Session</label>
                  <select
                    value={questionData.session || ''}
                    onChange={(e) => updateField('session', e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200"
                  >
                    <option value="">Not specified</option>
                    <option value="May/June">May/June</option>
                    <option value="Oct/Nov">Oct/Nov</option>
                    <option value="Feb/Mar">Feb/Mar</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Variant</label>
                  <select
                    value={questionData.variant || ''}
                    onChange={(e) => updateField('variant', e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200"
                  >
                    <option value="">Not specified</option>
                    <option value="1">Variant 1 (e.g., /41)</option>
                    <option value="2">Variant 2 (e.g., /42)</option>
                    <option value="3">Variant 3 (e.g., /43)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-6">
              <h2 className="text-lg font-semibold text-indigo-900 mb-4">Summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-indigo-700">Total Marks:</span>
                  <span className="font-bold text-indigo-900">{calculateTotalMarks()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-indigo-700">Type:</span>
                  <span className="font-medium text-indigo-900 capitalize">{questionData.question_type}</span>
                </div>
                {questionData.question_type === 'structured' && (
                  <div className="flex justify-between">
                    <span className="text-indigo-700">Parts:</span>
                    <span className="font-medium text-indigo-900">{(questionData.parts || []).length}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-indigo-700">Status:</span>
                  <span className={`font-medium ${questionData.status === 'published' ? 'text-green-700' : 'text-yellow-700'}`}>
                    {questionData.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Add Topic Modal */}
      {showAddTopicModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Quick Add Topic</h3>
              <button
                onClick={() => { setShowAddTopicModal(false); setNewTopicData({ name: '', code: '' }); }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleQuickAddTopic} className="p-4 space-y-4">
              <div className="bg-indigo-50 rounded-lg p-3 text-sm text-indigo-700">
                <p><strong>Board:</strong> {boards.find(b => b.id === questionData.board_id)?.name}</p>
                <p><strong>Subject:</strong> {subjects.find(s => s.id === questionData.subject_id)?.name}</p>
                {questionData.paper_id && (
                  <p><strong>Paper:</strong> {papers.find(p => p.id === questionData.paper_id)?.name}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Topic Code (Optional)</label>
                <input
                  type="text"
                  value={newTopicData.code}
                  onChange={(e) => setNewTopicData(prev => ({ ...prev, code: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., T1, T2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Topic Name *</label>
                <input
                  type="text"
                  required
                  value={newTopicData.name}
                  onChange={(e) => setNewTopicData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Motion, forces and energy"
                  autoFocus
                />
              </div>
              
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddTopicModal(false); setNewTopicData({ name: '', code: '' }); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingTopic}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium rounded-lg transition-all"
                >
                  <Plus className="w-4 h-4" />
                  {addingTopic ? 'Creating...' : 'Create Topic'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Add Subtopic Modal */}
      {showAddSubtopicModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Quick Add Subtopic</h3>
              <button
                onClick={() => { setShowAddSubtopicModal(false); setNewSubtopicData({ name: '', code: '' }); }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleQuickAddSubtopic} className="p-4 space-y-4">
              <div className="bg-green-50 rounded-lg p-3 text-sm text-green-700">
                <p><strong>Topic:</strong> {topics.find(t => t.id === questionData.topic_id)?.name}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subtopic Code (Optional)</label>
                <input
                  type="text"
                  value={newSubtopicData.code}
                  onChange={(e) => setNewSubtopicData(prev => ({ ...prev, code: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., ST1, ST2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subtopic Name *</label>
                <input
                  type="text"
                  required
                  value={newSubtopicData.name}
                  onChange={(e) => setNewSubtopicData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Physical quantities and measurement techniques"
                  autoFocus
                />
              </div>
              
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddSubtopicModal(false); setNewSubtopicData({ name: '', code: '' }); }}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingSubtopic}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded-lg transition-all"
                >
                  <Plus className="w-4 h-4" />
                  {addingSubtopic ? 'Creating...' : 'Create Subtopic'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionEditor;
